import { ChangeDetectionStrategy, Component, computed, input, signal } from '@angular/core';
import { RenderedBlock, ToolCallStatus } from '../agent-conversation.model';
import { buildGenericView, buildToolResultView } from '../agent-tool-result';
import { toolErrorKey, toolLabel } from '../agent-tool-result-format';
import { ToolResultView } from '../agent-tool-result.model';

@Component({
  selector: 'app-agent-tool-call',
  templateUrl: './agent-tool-call.component.html',
  styleUrls: ['./agent-tool-call.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentToolCallComponent {
  readonly block = input.required<RenderedBlock>();

  protected readonly expanded = signal(false);

  protected readonly status = computed<ToolCallStatus>(() => this.block().toolStatus ?? 'running');
  // An i18n key for a known tool (resolved by the translate pipe), or a humanized name for the rest.
  protected readonly displayName = computed(() => toolLabel(this.block().toolName ?? ''));
  protected readonly statusIcon = computed(() => STATUS_ICONS[this.status()]);
  protected readonly inputJson = computed(() => stringify(this.block().input));
  protected readonly resultJson = computed(() => stringify(this.block().content));

  protected readonly errorKey = computed(() => {
    const block = this.block();
    return block.toolStatus === 'error' ? toolErrorKey(block.content) : '';
  });

  // Formatted view of a successful result: a curated per-tool view if one exists, otherwise the
  // structural generic fallback. Undefined (bare scalar / empty) falls back to raw JSON in the template.
  protected readonly resultView = computed<ToolResultView | undefined>(() => {
    const block = this.block();
    if (block.toolStatus !== 'done') {
      return undefined;
    }
    return buildToolResultView(block.toolName ?? '', block.content) ?? buildGenericView(block.content);
  });

  // Formatted view of the request arguments; undefined falls back to raw JSON (e.g. a no-arg call).
  protected readonly inputView = computed<ToolResultView | undefined>(() => buildGenericView(this.block().input));

  // Raw-JSON escape hatch, offered only once something rendered as a formatted view (otherwise the raw
  // JSON is already what's on screen).
  protected readonly canShowRaw = computed(() => this.inputView() !== undefined || this.resultView() !== undefined);
  protected readonly showRaw = signal(false);

  protected toggle(): void {
    this.expanded.update((value) => !value);
  }

  protected toggleRaw(): void {
    this.showRaw.update((value) => !value);
  }
}

const STATUS_ICONS: Record<ToolCallStatus, string> = {
  running: 'autorenew',
  done: 'check_circle',
  error: 'error',
  refused: 'info',
  // Confirmation-gated: not run yet; the run shows as a separate call after approval.
  awaiting: 'pause_circle',
  // The user declined the confirmation; this call will not run.
  declined: 'block',
  skipped: 'skip_next',
};

function stringify(value: unknown): string {
  if (value === undefined || value === null) {
    return '';
  }
  if (typeof value === 'string') {
    return value;
  }
  return JSON.stringify(value, null, 2);
}
